<script setup lang="ts">
    import { computed, ref, watch } from 'vue';

    import EarlyAccessSuccessDialog from '@/pages/Landing/islands/components/EarlyAccessSuccessDialog.vue';
    import LandingContactDialogIsland from '@/pages/Landing/islands/components/LandingContactDialogIsland.vue';
    import { validateEmail } from '@/pages/Landing/utils/landingValidators';
    import { landingPageInquiryService } from '@/services/LandingPageInquiryService';
    import { waitlistEntryService } from '@/services/WaitlistEntryService';
    import { fireConfetti } from '@/utils/fireConfetti';

    const props = withDefaults(
        defineProps<{
            layout?: 'inline' | 'stacked';
        }>(),
        {
            layout: 'inline',
        },
    );

    const email = ref('');
    const submitting = ref(false);
    const submitted = ref(false);
    const alreadyRegistered = ref(false);
    const error = ref<string | null>(null);

    const dialogOpen = ref(false);
    const contactDialogOpen = ref(false);
    const comments = ref('');
    const contactAboutBeta = ref(false);
    const feedbackSubmitting = ref(false);
    const feedbackSubmitted = ref(false);
    const feedbackError = ref<string | null>(null);

    const emailError = computed(() => validateEmail(email.value));
    const isEmailValid = computed(() => emailError.value === null);
    const canSubmit = computed(() => !submitting.value && emailError.value === null);
    const canSubmitFeedback = computed(() => {
        if (feedbackSubmitting.value) return false;
        if (alreadyRegistered.value) {
            return comments.value.trim().length > 0;
        }

        return comments.value.trim().length > 0 || contactAboutBeta.value;
    });

    const disabledTooltip = computed(() => {
        if (submitting.value) return null;
        if (email.value.trim().length === 0) return 'Please enter an email';
        return null;
    });

    const validPulse = ref(false);
    watch(isEmailValid, (next, prev) => {
        if (next && !prev) {
            validPulse.value = true;
            window.setTimeout(() => {
                validPulse.value = false;
            }, 520);
        }
    });

    function openDialog() {
        dialogOpen.value = true;
        feedbackError.value = null;
        feedbackSubmitted.value = false;
    }

    function openContactDialog() {
        contactDialogOpen.value = true;
    }

    async function submit() {
        error.value = null;

        if (!canSubmit.value) {
            error.value = emailError.value ?? 'Enter a valid email address.';
            return;
        }

        submitting.value = true;

        try {
            const response = await waitlistEntryService.joinWaitlist(email.value.trim(), null, false);
            alreadyRegistered.value = response.already_registered;
            submitted.value = true;
            dialogOpen.value = true;
            feedbackError.value = null;
            feedbackSubmitted.value = false;
            comments.value = '';
            contactAboutBeta.value = false;

            if (!alreadyRegistered.value) {
                setTimeout(() => fireConfetti(), 300);
            }
        } catch (err: any) {
            error.value = err?.response?.data?.message ?? 'Unable to join the waitlist. Please try again.';
        } finally {
            submitting.value = false;
        }
    }

    async function submitFeedback() {
        feedbackError.value = null;

        const normalizedComments = comments.value.trim() ? comments.value.trim() : null;
        if (!normalizedComments && !contactAboutBeta.value) {
            dialogOpen.value = false;
            return;
        }

        feedbackSubmitting.value = true;
        try {
            const messageParts: string[] = [];
            if (normalizedComments) {
                messageParts.push(normalizedComments);
            }

            if (contactAboutBeta.value) {
                messageParts.push('I’d like to be contacted about participating in the beta.');
            }

            const message = messageParts.join('\n\n').trim();
            await landingPageInquiryService.submitInquiry(email.value.trim(), message);

            if (!alreadyRegistered.value) {
                await waitlistEntryService.joinWaitlist(email.value.trim(), normalizedComments, contactAboutBeta.value);
            }

            feedbackSubmitted.value = true;
        } catch (err: any) {
            feedbackError.value =
                err?.response?.data?.message ??
                (alreadyRegistered.value ? 'Unable to send your message. Please try again.' : 'Unable to submit feedback. Please try again.');
        } finally {
            feedbackSubmitting.value = false;
        }
    }
</script>

<template>
    <div :class="['early-access', { 'early-access--stacked': props.layout === 'stacked' }]">
        <form v-if="!submitted" class="early-access__form" @submit.prevent="submit">
            <div class="early-access__row" role="group" aria-label="Early access signup">
                <input
                    id="landing-early-access-email"
                    style="min-width: 300px"
                    v-model="email"
                    class="early-access__input"
                    type="email"
                    name="email"
                    autocomplete="email"
                    inputmode="email"
                    placeholder="you@bakery.com"
                    :disabled="submitting"
                    required />
                <span class="early-access__button-wrap">
                    <span v-if="disabledTooltip" class="early-access__tooltip" role="tooltip">{{ disabledTooltip }}</span>
                    <button
                        :class="[
                            'early-access__button',
                            {
                                'early-access__button--ready': canSubmit,
                                'early-access__button--pulse': validPulse,
                            },
                        ]"
                        type="submit"
                        :disabled="!canSubmit">
                        <span v-if="submitting">Sending…</span>
                        <span v-else>Get Early Access</span>
                    </button>
                </span>
            </div>
            <p v-if="error" class="early-access__error" role="alert">{{ error }}</p>
        </form>

        <div v-else class="early-access__success" role="status">
            <p class="early-access__success-text">Thanks, you're on the early access list!</p>
            <button class="early-access__button early-access__contact-button" type="button" @click="openContactDialog">Contact Us</button>
        </div>

        <EarlyAccessSuccessDialog
            v-model="dialogOpen"
            v-model:comments="comments"
            v-model:contactAboutBeta="contactAboutBeta"
            :already-registered="alreadyRegistered"
            :submitted="feedbackSubmitted"
            :submitting="feedbackSubmitting"
            :can-submit-feedback="canSubmitFeedback"
            :error="feedbackError"
            @submit="submitFeedback" />

        <LandingContactDialogIsland v-model="contactDialogOpen" :initial-email="email" />
    </div>
</template>

<style scoped lang="scss">
    .early-access {
        max-width: 600px;
        margin: 18px auto 0;
        text-align: center;

        &__row {
            display: flex;
            align-items: stretch;
            gap: 12px;
            justify-content: center;
        }

        &__button-wrap {
            position: relative;
            display: inline-flex;
        }

        &__tooltip {
            position: absolute;
            left: 50%;
            top: -10px;
            transform: translate(-50%, -100%) translateY(8px);
            opacity: 0;
            transition:
                opacity 0.16s ease,
                transform 0.16s ease;
            pointer-events: none;

            background: var(--landing-bg);
            color: var(--landing-text);
            border: 1px solid var(--landing-border);
            box-shadow: 0 18px 34px rgba(0, 0, 0, 0.25);
            border-radius: 10px;
            padding: 8px 10px;
            font-size: 13px;
            font-weight: 600;
            white-space: nowrap;
            z-index: 5;
        }

        &__button-wrap:hover &__tooltip {
            opacity: 1;
            transform: translate(-50%, -100%) translateY(0);
        }

        &--stacked {
            max-width: 100%;

            .early-access__row {
                flex-direction: column;
                align-items: stretch;
            }

            .early-access__input {
                flex: 1 1 auto;
            }

            .early-access__button-wrap {
                width: 100%;
            }

            .early-access__button {
                width: 100%;
            }
        }

        &__input {
            flex: 1 1 350px;
            min-width: 0;
            padding: 14px 16px;
            border: 1px solid rgba(226, 232, 240, 0.9);
            border-radius: 0.75em;
            background: rgba(255, 255, 255, 0.9);
            font-size: 16px;
            box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
            outline: none;
            transition:
                box-shadow 0.15s ease,
                border-color 0.15s ease,
                background-color 0.15s ease;

            &:focus {
                border-color: rgba(114, 87, 209, 0.55);
                box-shadow:
                    0 10px 26px rgba(15, 23, 42, 0.06),
                    0 0 0 4px rgba(114, 87, 209, 0.14);
            }

            &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }
        }

        &__button {
            flex: 0 0 auto;
            min-width: 200px;
            padding: 14px 18px;
            border: 0;
            border-radius: 0.5em;
            background: var(--landing-accent);
            color: #ffffff;
            font-weight: 700;
            font-size: 16px;
            cursor: pointer;
            box-shadow: 0 14px 30px rgba(114, 87, 209, 0.22);
            transition:
                transform 0.1s ease,
                box-shadow 0.2s ease,
                filter 0.2s ease,
                opacity 0.2s ease;

            &:hover {
                filter: brightness(1.03);
                box-shadow: 0 16px 34px rgba(114, 87, 209, 0.26);
            }

            &:active {
                transform: translateY(1px);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
                box-shadow: none;
            }

            &--ready {
                position: relative;
                overflow: hidden;
                animation: early-access-ready-glow 2.8s ease-in-out infinite;

                &::before {
                    content: '';
                    position: absolute;
                    inset: -3px;
                    background:
                        radial-gradient(circle at 30% 0%, rgba(255, 255, 255, 0.35), transparent 55%),
                        linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.22) 42%, transparent 60%);
                    transform: translateX(-60%);
                    opacity: 0;
                    pointer-events: none;
                    animation: early-access-ready-shimmer 3.8s ease-in-out infinite;
                }
            }

            &--pulse {
                animation: early-access-pulse 520ms ease-out;
            }

            &--ready#{&}--pulse {
                animation:
                    early-access-ready-glow 2.8s ease-in-out infinite,
                    early-access-pulse 520ms ease-out;
            }
        }

        &__error {
            margin: 10px 0 0;
            font-size: 14px;
            color: #b91c1c;
        }

        &__success {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin: 14px 0 0;
        }

        &__success-text {
            margin: 0;
            font-weight: 800;
            color: var(--landing-text);
        }

        &__contact-button {
            padding-left: 22px;
            padding-right: 22px;
        }
    }

    @keyframes early-access-pulse {
        0% {
            transform: translateY(0) scale(1);
            box-shadow: 0 14px 30px rgba(114, 87, 209, 0.22);
            filter: brightness(1);
        }
        45% {
            transform: translateY(-1px) scale(1.03);
            box-shadow:
                0 18px 44px rgba(114, 87, 209, 0.35),
                0 0 0 6px rgba(114, 87, 209, 0.14);
            filter: brightness(1.08);
        }
        100% {
            transform: translateY(0) scale(1);
            box-shadow: 0 14px 30px rgba(114, 87, 209, 0.22);
            filter: brightness(1);
        }
    }

    @keyframes early-access-ready-glow {
        0%,
        100% {
            box-shadow: 0 14px 30px rgba(114, 87, 209, 0.22);
            filter: brightness(1);
        }
        50% {
            box-shadow:
                0 18px 44px rgba(114, 87, 209, 0.3),
                0 0 0 2px rgba(255, 255, 255, 0.08);
            filter: brightness(1.04);
        }
    }

    @keyframes early-access-ready-shimmer {
        0% {
            opacity: 0;
            transform: translateX(-70%);
        }
        15% {
            opacity: 0.8;
        }
        45% {
            opacity: 0;
            transform: translateX(70%);
        }
        100% {
            opacity: 0;
            transform: translateX(70%);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .early-access__button--ready,
        .early-access__button--pulse {
            animation: none !important;
        }

        .early-access__button--ready::before {
            animation: none !important;
            display: none;
        }
    }

    @media (max-width: 520px) {
        .early-access {
            &__row {
                flex-direction: column;
                align-items: stretch;
            }

            &__input {
                flex-basis: auto;
            }

            &__button {
                width: 100%;
                min-width: 0;
            }

            &__button-wrap {
                width: 100%;
            }

            &__contact-button {
                width: 100%;
            }
        }
    }
</style>
